import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import LopdDocuments from "@/components/LopdDocuments";
import { toast } from "sonner";
import { ShieldCheck, FileSignature, Users, CheckCircle2, Clock, Loader2, ScrollText } from "lucide-react";

export default function LopdCompany() {
  const [data, setData] = useState(null);
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  const load = useCallback(async () => {
    const [c, cons] = await Promise.all([api.get("/lopd/company"), api.get("/lopd/consents")]);
    setData(c.data); setConsents(cons.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const acceptDpa = async () => {
    setAccepting(true);
    try {
      await api.post("/lopd/company/accept-dpa");
      toast.success("Contrato de Encargado del Tratamiento aceptado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAccepting(false); }
  };

  if (loading) return <div className="h-64 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-300" /></div>;

  const pct = data.total_employees ? Math.round((data.consented / data.total_employees) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Protección de datos (LOPD/RGPD)</h1>
        <p className="text-slate-500 text-sm mt-1">Gestiona el cumplimiento del RGPD: contrato de tratamiento, documentos legales y consentimientos.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* DPA */}
        <Card className="lg:col-span-1 p-6 border-slate-200 shadow-none">
          <div className="flex items-center gap-2 mb-4"><FileSignature className="h-5 w-5 text-sky-500" /><h3 className="font-heading font-semibold">Contrato de Tratamiento</h3></div>
          {data.dpa_accepted ? (
            <div className="text-center py-4">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-2" />
              <div className="font-semibold text-slate-800">DPA aceptado</div>
              <div className="text-xs text-slate-400 mt-1">Por {data.dpa_accepted_by}<br />{data.dpa_accepted_at && new Date(data.dpa_accepted_at).toLocaleString("es-ES")}</div>
            </div>
          ) : (
            <div className="text-center py-2">
              <p className="text-sm text-slate-600 mb-4">Acepta el Contrato de Encargado del Tratamiento (art. 28 RGPD) entre tu empresa y Fichando para activar el cumplimiento.</p>
              <Button onClick={acceptDpa} disabled={accepting} className="brand-btn w-full" data-testid="accept-dpa-btn">
                {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aceptar DPA"}
              </Button>
            </div>
          )}
        </Card>

        {/* Consent stats */}
        <Card className="lg:col-span-2 p-6 border-slate-200 shadow-none">
          <div className="flex items-center gap-2 mb-4"><Users className="h-5 w-5 text-sky-500" /><h3 className="font-heading font-semibold">Consentimientos de empleados</h3></div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div><div className="font-heading text-3xl font-bold">{data.total_employees}</div><div className="text-xs text-slate-400 uppercase tracking-wide font-bold">Empleados</div></div>
            <div><div className="font-heading text-3xl font-bold text-emerald-600">{data.consented}</div><div className="text-xs text-slate-400 uppercase tracking-wide font-bold">Aceptados</div></div>
            <div><div className="font-heading text-3xl font-bold text-amber-500">{data.pending}</div><div className="text-xs text-slate-400 uppercase tracking-wide font-bold">Pendientes</div></div>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
          <div className="text-xs text-slate-400 mt-1.5">{pct}% del equipo ha aceptado la cláusula informativa.</div>
        </Card>
      </div>

      {/* Documents */}
      <Card className="p-6 border-slate-200 shadow-none">
        <div className="flex items-center gap-2 mb-4"><ScrollText className="h-5 w-5 text-sky-500" /><h3 className="font-heading font-semibold">Documentos legales (plantillas RGPD España)</h3></div>
        <LopdDocuments documents={data.documents} />
      </Card>

      {/* Consent table */}
      <Card className="border-slate-200 shadow-none overflow-hidden">
        <div className="p-5 border-b border-slate-100"><h3 className="font-heading font-semibold">Registro de consentimientos</h3></div>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Empleado</TableHead><TableHead>Departamento</TableHead><TableHead>Estado</TableHead><TableHead>Fecha de aceptación</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {consents.map((c) => (
              <TableRow key={c.id} data-testid={`consent-row-${c.id}`}>
                <TableCell><div className="font-medium text-slate-800">{c.name}</div><div className="text-xs text-slate-400">{c.email}</div></TableCell>
                <TableCell className="text-slate-600">{c.department}</TableCell>
                <TableCell>{c.consent ? <Badge className="bg-emerald-100 text-emerald-700 border-0"><CheckCircle2 className="h-3 w-3 mr-1" /> Aceptado</Badge> : <Badge className="bg-amber-100 text-amber-700 border-0"><Clock className="h-3 w-3 mr-1" /> Pendiente</Badge>}</TableCell>
                <TableCell className="text-slate-500 text-sm">{c.consent_at ? new Date(c.consent_at).toLocaleString("es-ES") : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
